/**
 * Input boundary. Input is expressed to the game as abstract, device-neutral
 * intents — a movement direction (with analog throttle) and counts of discrete
 * action activations since the last drain — NOT raw mouse/touch/key events.
 * Desktop and mobile both implement InputSource and feed the exact same game
 * logic, so controls are swappable and new schemes are additive.
 */
import type { Vec2 } from '../core/math/vec2';
import type { Entity } from '../core/types';

export interface InputFrame {
  /** Desired movement direction; magnitude is the analog throttle in [0, 1]. */
  moveDir: Vec2;
  /** Discrete activations accumulated since the last drain, keyed by action id. */
  pulses: Record<string, number>;
}

export interface InputSource {
  start(): void;
  stop(): void;
  /** Return and reset the accumulated input since the previous call. */
  drain(): InputFrame;
}

export function emptyFrame(): InputFrame {
  return { moveDir: { x: 0, y: 0 }, pulses: {} };
}

/** Apply a drained input frame onto the player entity's intent for this frame. */
export function applyFrameToIntent(entity: Entity, frame: InputFrame): void {
  entity.intent.moveDir = frame.moveDir;
  for (const key in frame.pulses) {
    entity.intent.pulses[key] = (entity.intent.pulses[key] ?? 0) + frame.pulses[key];
  }
}
